import { create } from 'zustand';

import { singleFlight } from '@/shared/lib';

import { fetchPendingResult, markResultSeen } from '../api/results.api';

import type { PeriodResult } from './types';

/**
 * Итог периода, который игрок ещё не видел.
 *
 * Сервер отдаёт по одному, от крупного периода к мелкому. После закрытия
 * запрашиваем следующий — очередь живёт на сервере, клиент про порядок
 * не знает.
 */
interface ResultsState {
  pending: PeriodResult | null;
  loadedAt: number | null;

  load: (options?: { force?: boolean }) => Promise<void>;
  dismiss: () => void;
  reset: () => void;
}

export const useResultsStore = create<ResultsState>()((set, get) => ({
  pending: null,
  loadedAt: null,

  load: singleFlight(async (options?: { force?: boolean }): Promise<void> => {
    if (!options?.force && get().loadedAt !== null) return;

    try {
      const response = await fetchPendingResult();
      set({
        pending: response.hasResult ? response.result : null,
        loadedAt: Date.now(),
      });
    } catch {
      // Молча: итоги — приятный бонус, а не содержимое экрана. Красная
      // плашка вместо поздравления хуже, чем отсутствие поздравления.
      set({ loadedAt: Date.now() });
    }
  }),

  /**
   * Оптимистично: сначала убираем модалку, потом сообщаем серверу
   * и просим следующий итог. Упавший POST означает, что игрок увидит
   * тот же итог в следующий заход, — это лучше, чем модалка, которую
   * нельзя закрыть из-за сети.
   */
  dismiss: () => {
    const current = get().pending;
    set({ pending: null });
    if (!current) return;

    void markResultSeen({ kind: current.kind, refId: current.refId })
      .then(() => get().load({ force: true }))
      .catch(() => undefined);
  },

  reset: () => {
    set({ pending: null, loadedAt: null });
  },
}));
